<script setup lang="ts">
import { useFormValidation } from "../../../../composables/useFormValidation";
import { required } from "../../../../utils/validation";
import { api } from "../../../../api/Api";
import Toast from "../../../../services/Toast";
import { ref, onMounted, computed } from "vue";
import IconComponent from "../../../../components/ui/common/IconComponent.vue";
import FileUpload from "../../../../components/ui/form/FileUpload.vue";
import TextInput from "../../../../components/ui/form/TextInput.vue";
import TextAreaInput from "../../../../components/ui/form/TextAreaInput.vue";
import GroupedSelect from "../../../../components/ui/form/GroupedSelect.vue";
import NumberInput from "../../../../components/ui/form/NumberInput.vue";
import PhoneInput from "../../../../components/ui/form/PhoneInput.vue";

const props = defineProps<{
  data?: any | null;
  listCreateName?: string;
}>();

const endPoint = "/clients/system/company/company-information/";
const data = ref<any[]>([]);

const mode = ref("view");

const emit = defineEmits<{
  (e: "save", data: any): void;
  (e: "close"): void;
}>();

const isReadOnly = computed(() => mode.value === "view");

const { fields, formData, setFieldRules, validateForm, setFormData } =
  useFormValidation({
    logo: props.data?.logo || "",
    name: props.data?.logo || "",
    tax_identification_no: props.data?.tax_identification_no || "",
    prefix: props.data?.logo || "",
    suffix: props.data?.logo || "",
    address: props.data?.logo || "",
    buildings_id: props.data?.logo || "",
    lng: props.data?.logo || "",
    lat: props.data?.logo || "",
    tel: props.data?.logo || "",
    mobile: props.data?.logo || "",
    viber: props.data?.logo || "",
    whatsapp: props.data?.logo || "",
    email: props.data?.logo || "",
    url: props.data?.logo || "",
    letterhead: props.data?.logo || "",
    comp_stamp: props.data?.comp_stamp || "",
    active: props.data?.active || true,
    uid: props.data?.uid || "",
    footer_text: props.data?.footer_text || "",
  });

setFieldRules("name", [required("Name is required")]);
//setFieldRules('logo', [required('Logo is required')])
setFieldRules("mobile", [required("Mobile is required")]);
setFieldRules("email", [required("Email is required")]);

const handleSubmit = async () => {
  if (validateForm()) {
    const action = formData.value.uid != "" ? "update" : "create";
    const response = await api.post(
      (endPoint.slice(-1) == "/" ? endPoint : endPoint + "/") + action,
      formData.value
    );
    if (response.success == true) {
      mode.value = "view";
      Toast.show("saved successfully");
      emit("save", response.data);
      mode.value = 'view'
    }
  }
};

const handleDataLoad = async (showLoader: boolean = true) => {
  const apiData = {};
  const response = await api.post(endPoint + "get", apiData, {
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    skipLoading: !showLoader,
  });
  if (response.success == true) {
    setFormData(response.data);
  } else {
    data.value = [];
  }
};

onMounted(async () => {
  handleDataLoad();
});
</script>
<template>
  <div class="">
    <div
      class="flex-1 bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-100 dark:border-gray-800 p-6 overflow-y-auto mb-[200px]"
    >
      <div class="flex justify-between items-center mb-6" id="page-list-header">
        <h2 class="text-2xl font-semibold text-gray-800 dark:text-white flex">
          <IconComponent name="Banknote" class="mr-1 mt-1" />Company Information
        </h2>
        <div>
        <button
          @click="mode = 'edit'"
          v-if="isReadOnly"
          class="px-2 md:px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center justify-center"
        >
          <IconComponent name="Pencil" />
          <span class="ml-2 uppercase tracking-wide">Edit</span>
        </button>
        <button
          @click="handleSubmit"
          v-if="!isReadOnly"
          class="px-2 md:px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center justify-center"
        >
          <IconComponent name="Save" />
          <span class="ml-2 uppercase tracking-wide">Save</span>
        </button>
      </div>
      </div>
      <hr />
      <div>
        <div id="document-section">
          <FileUpload
            accept=".pdf,.png,.jpg,.jpeg,.txt,.xls,.xlsx,.dox,.docx,.html,.xml"
            label="Logo"
            :max-size="5"
            :multiple="false"
            :readonly="isReadOnly"
            :image="true"
            path="company_info"
            v-model="fields.logo.value"
          />
        </div>
        <div id="basic-info-section" class="grid md:grid-cols-2 gap-6">
          <TextInput
            v-model="fields.name.value"
            label="Name"
            :readonly="isReadOnly"
            :error="fields.name.touched ? fields.name.error : null"
            @blur="fields.name.touched = true"
          />

          <TextInput
            v-model="fields.tax_identification_no.value"
            label="TAX Identification No"
            :readonly="isReadOnly"
            :error="
              fields.tax_identification_no.touched
                ? fields.tax_identification_no.error
                : null
            "
            @blur="fields.tax_identification_no.touched = true"
          />

          <TextInput
            v-model="fields.prefix.value"
            label="Prefix"
            :readonly="isReadOnly"
            :error="fields.prefix.touched ? fields.prefix.error : null"
            @blur="fields.prefix.touched = true"
          />

          <TextInput
            v-model="fields.suffix.value"
            label="Suffix"
            :readonly="isReadOnly"
            :error="fields.suffix.touched ? fields.suffix.error : null"
            @blur="fields.suffix.touched = true"
          />

          <TextAreaInput
            v-model="fields.address.value"
            :rows="4"
            label="Address"
            :readonly="isReadOnly"
            :error="fields.address.touched ? fields.address.error : null"
            @blur="fields.address.touched = true"
          />

          <GroupedSelect
            v-model="fields.buildings_id.value"
            label="Building"
            end-point="system/master-data/buildings"
            optionLabel="buildings"
            :readonly="isReadOnly"
            :error="
              fields.buildings_id.touched ? fields.buildings_id.error : null
            "
            @blur="fields.buildings_id.touched = true"
          />

          <NumberInput
            v-model="fields.lat.value"
            label="Latitude"
            :readonly="isReadOnly"
            :min="0"
            :error="fields.lat.touched ? fields.lat.error : null"
            @blur="fields.lat.touched = true"
          />

          <NumberInput
            v-model="fields.lng.value"
            label="Longitude"
            :readonly="isReadOnly"
            :min="0"
            :error="fields.lng.touched ? fields.lng.error : null"
            @blur="fields.lng.touched = true"
          />

          <PhoneInput
            v-model="fields.tel.value"
            label="Phone"
            :readonly="isReadOnly"
            :error="fields.tel.touched ? fields.tel.error : null"
            @blur="fields.tel.touched = true"
          />

          <PhoneInput
            v-model="fields.mobile.value"
            label="Mobile"
            :readonly="isReadOnly"
            :error="fields.mobile.touched ? fields.mobile.error : null"
            @blur="fields.mobile.touched = true"
          />

          <PhoneInput
            v-model="fields.viber.value"
            label="Viber"
            :readonly="isReadOnly"
            :error="fields.viber.touched ? fields.viber.error : null"
            @blur="fields.viber.touched = true"
          />

          <PhoneInput
            v-model="fields.whatsapp.value"
            label="WhatsApp"
            :readonly="isReadOnly"
            :error="fields.whatsapp.touched ? fields.whatsapp.error : null"
            @blur="fields.whatsapp.touched = true"
          />

          <TextInput
            v-model="fields.email.value"
            label="Email"
            :readonly="isReadOnly"
            :error="fields.email.touched ? fields.email.error : null"
            @blur="fields.email.touched = true"
          />

          <TextInput
            v-model="fields.url.value"
            label="URL"
            :readonly="isReadOnly"
            :error="fields.url.touched ? fields.url.error : null"
            @blur="fields.url.touched = true"
          />

          <TextAreaInput
          v-model="fields.footer_text.value"
          label="Document Footer Text"
          :readonly="isReadOnly"
          :error="fields.footer_text.touched ? fields.footer_text.error : null"
          @blur="fields.footer_text.touched = true"
        />

        <div id="document-section">
          <FileUpload
            accept=".pdf,.png,.jpg,.jpeg,.txt,.xls,.xlsx,.dox,.docx,.html,.xml"
            label="Company Stamp"
            :max-size="5"
            :multiple="false"
            :readonly="isReadOnly"
            :image="true"
            path="company_info"
            v-model="fields.comp_stamp.value"
          />
        </div>

          <!--<CheckInput
            v-model="fields.active.value"
            :readonly="isReadOnly"
            label="Active"
            :error="fields.active.touched ? fields.active.error : null"
            @blur="fields.active.touched = true"
          />-->
        </div>
      </div>
    </div>
  </div>
</template>
